import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Icon from './Icon';
import { colorAccent, colorAdditional, colorBackgroundBody, colorBorder, colorHover, colorText, colorWhite } from '../theme/palette';

const CollapsibleCard = ({
  title,
  children,
  collapsible = true,
  defaultCollapsed = false,
  isCollapsed,
  onToggle,
  className,
  headerRight,
  maxContentHeight = '2000px'
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const collapsed = isCollapsed !== undefined ? isCollapsed : internalCollapsed;

  useEffect(() => {
    if (isCollapsed !== undefined) {
      setInternalCollapsed(isCollapsed);
    }
  }, [isCollapsed]);

  const toggleCollapse = useCallback(() => {
    if (!collapsible) return;
    const next = !collapsed;
    if (isCollapsed === undefined) {
      setInternalCollapsed(next);
    }
    if (onToggle) {
      onToggle(next);
    }
  }, [collapsed, collapsible, isCollapsed, onToggle]);

  const resolvedMaxHeight =
    typeof maxContentHeight === 'number' ? `${maxContentHeight}px` : maxContentHeight;

  return (
    <CardContainer className={className}>
      <CardHeader onClick={collapsible ? toggleCollapse : undefined}>
        <CardTitle>
          <Icon name="clipboard-text" size={16} />
          <span>{title}</span>
        </CardTitle>
        <HeaderActions>
          {headerRight}
          {collapsible && (
            <CollapseButton
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                toggleCollapse();
              }}
              collapsed={collapsed}
              title={collapsed ? 'Expandir' : 'Recolher'}
            >
              <Icon name={collapsed ? 'chevron-down' : 'chevron-up'} size={16} />
            </CollapseButton>
          )}
        </HeaderActions>
      </CardHeader>
      <CardContent collapsed={collapsed} maxContentHeight={resolvedMaxHeight}>
        {children}
      </CardContent>
    </CardContainer>
  );
};

CollapsibleCard.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
  collapsible: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onToggle: PropTypes.func,
  className: PropTypes.string,
  headerRight: PropTypes.node,
  maxContentHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

const CardContainer = styled.div`
  background: ${colorWhite};
  border: 1px solid ${colorBorder};
  border-radius: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: ${colorHover};
  border-bottom: 1px solid ${colorBorder};
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${colorBackgroundBody};
  }
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${colorText};
  font-size: 14px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CollapseButton = styled.button`
  background: none;
  border: none;
  color: ${colorAdditional};
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${colorWhite};
    color: ${colorText};
    transform: scale(1.05);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${colorAccent}40;
  }
`;

const CardContent = styled.div`
  max-height: ${(props) => (props.collapsed ? '0px' : props.maxContentHeight)};
  opacity: ${(props) => (props.collapsed ? '0' : '1')};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  padding: ${(props) => (props.collapsed ? '0 20px' : '20px')};
`;

export default CollapsibleCard;
